/**
 * Elevation profile and gradient colouring of a track.
 *
 * A profile is a smoothed [[m, ele], …] series ordered by distance along the
 * track (from lm-geoservices, or computed locally from GPX heights). The
 * gradient runs, the profile chart, the "highest" label and the route card
 * all read from it.
 */
/** Flip a profile for a reversed track. */
export function orientProfile(profile, lengthM, reversed) {
    if (!reversed)
        return profile;
    return profile
        .map(([m, ele]) => [Math.max(0, lengthM - m), ele])
        .reverse();
}
/** Rescale a profile's distances so it ends exactly at lengthM. */
export function scaleProfile(profile, lengthM) {
    const last = profile[profile.length - 1]?.[0] ?? 0;
    if (last <= 0)
        return profile;
    const k = lengthM / last;
    return profile.map(([m, ele]) => [m * k, ele]);
}
export function elevationAt(profile, m) {
    if (!profile.length)
        return null;
    if (m <= profile[0][0])
        return profile[0][1];
    const last = profile[profile.length - 1];
    if (m >= last[0])
        return last[1];
    let lo = 0;
    let hi = profile.length - 1;
    while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (profile[mid][0] <= m)
            lo = mid;
        else
            hi = mid;
    }
    const a = profile[lo];
    const b = profile[hi];
    const t = b[0] > a[0] ? (m - a[0]) / (b[0] - a[0]) : 0;
    return a[1] + (b[1] - a[1]) * t;
}
export function profileStats(profile) {
    if (!profile.length)
        return null;
    let ascent = 0;
    let descent = 0;
    let highest = { m: profile[0][0], ele: profile[0][1] };
    let lowest = { ...highest };
    for (let i = 1; i < profile.length; i++) {
        const diff = profile[i][1] - profile[i - 1][1];
        if (diff > 0)
            ascent += diff;
        else
            descent -= diff;
        if (profile[i][1] > highest.ele) {
            highest = { m: profile[i][0], ele: profile[i][1] };
        }
        if (profile[i][1] < lowest.ele) {
            lowest = { m: profile[i][0], ele: profile[i][1] };
        }
    }
    return {
        ascentM: ascent,
        descentM: descent,
        minEleM: lowest.ele,
        maxEleM: highest.ele,
        highest,
        lowest,
    };
}
/** Gradient in percent over a ±window metre span. */
export function gradeAt(profile, lengthM, m, windowM = 70) {
    const a = Math.max(0, m - windowM);
    const b = Math.min(lengthM, m + windowM);
    const run = b - a;
    if (run <= 0)
        return 0;
    const ea = elevationAt(profile, a);
    const eb = elevationAt(profile, b);
    if (ea == null || eb == null)
        return 0;
    return ((eb - ea) / run) * 100;
}
const G_FLAT = [140, 151, 148];
const G_UP = [232, 32, 60];
const G_DOWN = [30, 136, 255];
/** Below this gradient (%) the route is drawn flat grey. */
export const GRADE_FLAT_PCT = 3;
/** From this gradient (%) on the route has full colour. */
export const GRADE_FULL_PCT = 10;
/** Route colour for a gradient: grey when flat, red uphill, blue downhill. */
export function gradeColor(gradePct) {
    const a = Math.abs(gradePct);
    let t = Math.max(0, Math.min(1, (a - GRADE_FLAT_PCT) / (GRADE_FULL_PCT - GRADE_FLAT_PCT)));
    t = t * t * (3 - 2 * t);
    t = Math.round(t * 10) / 10;
    const target = gradePct > 0 ? G_UP : G_DOWN;
    const rgb = [0, 1, 2].map((i) => Math.round(G_FLAT[i] + (target[i] - G_FLAT[i]) * t));
    return `rgb(${rgb.join(",")})`;
}
export const GRADIENT_KEY = [
    { label: `Up ≥${GRADE_FULL_PCT} %`, color: gradeColor(GRADE_FULL_PCT) },
    { label: "Up 6 %", color: gradeColor(6) },
    { label: `Flat <${GRADE_FLAT_PCT} %`, color: gradeColor(0) },
    { label: "Down 6 %", color: gradeColor(-6) },
    { label: `Down ≥${GRADE_FULL_PCT} %`, color: gradeColor(-GRADE_FULL_PCT) },
];
/** Track split into runs of one colour (25 m steps, ±70 m window). */
export function gradientRuns(profile, lengthM, { stepM = 25, windowM = 70 } = {}) {
    if (!profile.length || lengthM <= 0) {
        return lengthM > 0
            ? [{ fromM: 0, toM: lengthM, color: gradeColor(0) }]
            : [];
    }
    const runs = [];
    for (let m = 0; m < lengthM; m += stepM) {
        const to = Math.min(lengthM, m + stepM);
        const color = gradeColor(gradeAt(profile, lengthM, (m + to) / 2, windowM));
        const current = runs[runs.length - 1];
        if (current && current.color === color)
            current.toM = to;
        else
            runs.push({ fromM: m, toM: to, color });
    }
    return runs;
}
/**
 * Local profile from the track's own heights, used when lm-geoservices is
 * unavailable: resampled every stepM and smoothed with a moving average.
 */
export function localProfile(coords, cum, { stepM = 10, smoothM = 60 } = {}) {
    const raw = [];
    coords.forEach((c, i) => {
        if (c[2] != null && Number.isFinite(c[2]))
            raw.push([cum[i], c[2]]);
    });
    if (raw.length < 2)
        return [];
    const length = cum[cum.length - 1];
    const sampled = [];
    for (let m = 0; m <= length; m += stepM) {
        sampled.push([m, elevationAt(raw, m)]);
    }
    if (sampled[sampled.length - 1][0] < length) {
        sampled.push([length, elevationAt(raw, length)]);
    }
    // smoothM 0: the file's heights as they are, only resampled
    if (smoothM <= 0) {
        return sampled.map(([m, ele]) => [m, Math.round(ele * 10) / 10]);
    }
    const half = Math.max(1, Math.round(smoothM / stepM / 2));
    return sampled.map(([m], i) => {
        let sum = 0;
        let n = 0;
        for (let j = Math.max(0, i - half); j <= Math.min(sampled.length - 1, i + half); j++) {
            sum += sampled[j][1];
            n++;
        }
        return [m, Math.round((sum / n) * 10) / 10];
    });
}
/** Where a track's heights come from: the file, a lookup, or both. */
export function elevationSourceOf(coords) {
    const withEle = coords.filter((c) => c[2] != null && Number.isFinite(c[2])).length;
    if (withEle === coords.length)
        return "file";
    if (withEle === 0)
        return "lookup";
    return "mixed";
}
